import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuthHeaders } from "../auth/apiClient";

export interface RouteConfig {
  id: number;
  tenant_id: number;
  code: string;
  name: string;
  source: "vonigo" | "custom";
  status: "active" | "paused" | "inactive";
  type: "tech" | "sales" | "virtual" | null;
  vonigo_route_id: number | null;
  needs_classification: boolean;
  territory: string | null;
  primary_collaborator_id: number | null;
  primary_collaborator_name: string | null;
  is_active: boolean;
}

async function fetchJson<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(path, init);
  if (!res.ok) throw new Error(`Failed to fetch ${path}: ${res.status}`);
  return res.json() as Promise<T>;
}

export function useRoutes() {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ["settings", "routes"],
    queryFn: () => fetchJson<{ items: RouteConfig[] }>("/api/cockpit/settings/routes", { headers }),
  });
}

export function useRouteMutations() {
  const headers = useAuthHeaders(true);
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["settings", "routes"] });
  return {
    create: useMutation({
      mutationFn: (payload: Partial<RouteConfig>) => fetchJson<RouteConfig>("/api/cockpit/settings/routes", {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      }),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, payload }: { id: number; payload: Partial<RouteConfig> }) => fetchJson<RouteConfig>(`/api/cockpit/settings/routes/${id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(payload),
      }),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: number) => fetchJson<RouteConfig>(`/api/cockpit/settings/routes/${id}`, {
        method: "DELETE",
        headers,
      }),
      onSuccess: invalidate,
    }),
  };
}
